import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { NAV_LINKS, FAQS, PROCESS_STEPS, PRODUCTS } from "./site";
import { SOLUTIONS } from "./solutions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export { NAV_LINKS };

const tel = (display) => {
  const d = String(display || "").replace(/\D/g, "");
  return "tel:+" + (d.length === 10 ? "91" + d : d);
};

const HERO = [
  "https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.pexels.com/photos/29923348/pexels-photo-29923348.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];
const PM_IMG = "https://images.unsplash.com/photo-1724041875334-0a6397111c7e?crop=entropy&cs=srgb&fm=jpg&q=85";
const PRODUCTS_IMG = "https://images.unsplash.com/photo-1558449028-b53a39d100fc?crop=entropy&cs=srgb&fm=jpg&q=85";

export const DEFAULT_CONTENT = {
  site: {
    tagline: "Power Your Future with Solar Energy",
    phone1Display: "98949 80894",
    phone2Display: "99424 08143",
    whatsappNumber: "919894980894",
    whatsappMessage:
      "Hello DHIYAA SOLAR ENERGY, I am interested in installing a solar system. Please contact me and provide a quotation.",
    emails: ["info@dhiyaasolarenergy.in", "md@dhiyaasolarenergy.in", "dhiyaasolarenergy@gmail.com"],
    website: "dhiyaasolarenergy.in",
    addressLines: [
      "27/1A, Church Cross Street",
      "Church Road, Radha Nagar",
      "Chromepet, Chennai – 600 044",
      "Tamil Nadu, India",
    ],
    gstin: "33AJWPP1862A1Z6",
    udyam: "UDYAM-TN22-0113483",
    mapsQuery: "27/1A Church Cross Street Radha Nagar Chromepet Chennai 600044",
  },
  images: {
    hero: HERO,
    pmSurya: PM_IMG,
    products: PRODUCTS_IMG,
    residential: "https://images.unsplash.com/photo-1658298775754-5839ffd434cc?crop=entropy&cs=srgb&fm=jpg&q=85",
    commercial: "https://images.pexels.com/photos/29923357/pexels-photo-29923357.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    industrial: HERO[2],
    pump: HERO[0],
  },
  home: {
    slides: [
      {
        image: HERO[0],
        eyebrow: "DHIYAA SOLAR ENERGY — CHENNAI",
        lines: ["POWER YOUR FUTURE", "WITH SOLAR ENERGY"],
        sub: "Reliable Solar Power Solutions for Homes, Businesses, Industries & Agriculture",
      },
      {
        image: HERO[1],
        eyebrow: "CLEAN ENERGY. SMART SAVINGS.",
        lines: ["TURN SUNLIGHT", "INTO SAVINGS"],
        sub: "Smart solar solutions for a brighter tomorrow — reduce your dependence on grid power.",
      },
      {
        image: HERO[2],
        eyebrow: "COMMERCIAL & INDUSTRIAL SOLAR",
        lines: ["SOLAR POWER FOR", "YOUR BUSINESS"],
        sub: "Reduce grid dependence with efficient, professionally engineered solar solutions.",
      },
      {
        image: PM_IMG,
        eyebrow: "PM SURYA GHAR: MUFT BIJLI YOJANA",
        lines: ["MAKE YOUR HOME", "A SOLAR HOME"],
        sub: "Central Financial Assistance for eligible residential rooftop solar installations.",
      },
    ],
    highlights: [
      { title: "Solar Rooftop Solutions", desc: "Generate clean electricity from your rooftop." },
      { title: "Reduce Electricity Costs", desc: "Use solar energy to reduce dependence on grid power." },
      { title: "Solar Pump Solutions", desc: "Efficient solar-powered pumping for agricultural and water applications." },
      { title: "Professional Installation", desc: "End-to-end installation, commissioning and support." },
    ],
    whyUs: [
      { title: "Quality Solar Components", desc: "Solar solutions using reputed and reliable components selected according to project requirements." },
      { title: "Professional Engineering", desc: "Proper system sizing, electrical design, installation and commissioning." },
      { title: "Complete Solar Solutions", desc: "From site assessment and system design to installation and after-sales support — end to end." },
      { title: "Transparent Approach", desc: "System capacity, components, expected generation and requirements explained clearly before installation." },
      { title: "Residential to Industrial", desc: "Solutions for homes, commercial buildings, institutions, agriculture and industrial applications." },
      { title: "Customer Support", desc: "We remain available for service, maintenance and technical assistance after installation." },
    ],
    pmSubsidy: [
      { v: "₹30,000", l: "per kW for first 2 kW" },
      { v: "₹18,000", l: "per kW for the 3rd kW" },
      { v: "₹78,000", l: "max central assistance" },
    ],
    pmSteps: ["Register", "Feasibility", "Select Vendor", "Solar Installation", "Inspection & Metering", "Subsidy Processing"],
  },
  about: {
    paragraphs: [
      "DHIYAA SOLAR ENERGY is a solar energy solutions company based in Chromepet, Chennai, Tamil Nadu, providing solar power and solar pumping solutions for residential, commercial, industrial and agricultural applications.",
      "Our primary focus is on helping customers adopt solar energy through properly designed, professionally installed and dependable solar systems — from residential rooftop systems to commercial and industrial solar plants and solar pump sets.",
      "Our approach combines appropriate technology, quality products, professional installation and customer support.",
    ],
    mission:
      "To make clean and reliable solar energy accessible to homes, businesses, industries and agricultural users through quality products and professional service.",
    vision:
      "To contribute to a cleaner and more sustainable future by accelerating the adoption of solar energy across Tamil Nadu and beyond.",
    values: [
      { title: "Quality", desc: "We focus on dependable products and proper installation." },
      { title: "Integrity", desc: "We believe in transparent communication with our customers." },
      { title: "Innovation", desc: "We continuously explore better solar technologies and solutions." },
      { title: "Customer Satisfaction", desc: "Our relationship with the customer continues beyond installation." },
      { title: "Sustainability", desc: "We promote clean energy for a better future." },
    ],
  },
  pmSurya: {
    intro:
      "The PM Surya Ghar: Muft Bijli Yojana is the Government of India's rooftop solar programme for residential consumers. The scheme provides Central Financial Assistance (CFA) for eligible residential rooftop solar installations.",
    stateNote:
      "Tamil Nadu's solar portal also describes a state-level additional subsidy structure and the process for eligible residential customers. Customers should verify their eligibility and the latest applicable subsidy rules before making a purchase decision.",
    note:
      "Tamil Nadu's official portal describes the sequence from national-portal application through TNPDCL feasibility, vendor selection, installation, inspection and subsidy processing. The portal refers to TEDA-empanelled vendors for the scheme — always verify the current empanelment and eligibility on the official portals.",
    subsidy: [
      { v: "₹30,000", l: "per kW — first 2 kW" },
      { v: "₹18,000", l: "per kW — additional 1 kW" },
      { v: "3 kW", l: "CFA cap — no central CFA beyond" },
    ],
    steps: [
      { title: "Register", desc: "Register through the official PM Surya Ghar national portal." },
      { title: "Feasibility", desc: "The applicable electricity distribution authority processes the feasibility requirements." },
      { title: "Select Vendor", desc: "Select an eligible/empanelled vendor applicable to your location and scheme." },
      { title: "Solar Installation", desc: "The solar system is installed according to applicable technical requirements." },
      { title: "Inspection & Metering", desc: "Required inspection and metering procedures are completed." },
      { title: "Subsidy Processing", desc: "Eligible subsidy/CFA is processed according to the applicable scheme guidelines." },
    ],
  },
  faqs: FAQS,
  products: PRODUCTS,
  brands: [
    { name: "WAAREE", logo: "/assets/brands/waaree.png" },
    { name: "UTL", logo: "/assets/brands/utl.png" },
    { name: "ADANI SOLAR", logo: "/assets/brands/adani-solar.png" },
    { name: "VIKRAM SOLAR", logo: "/assets/brands/vikram-solar.png" },
    { name: "RENEW", logo: "/assets/brands/renew.png" },
  ],
  processSteps: PROCESS_STEPS,
  projects: [
    {
      src: "/assets/project-chromepet.jpeg",
      type: "Residential Rooftop Solar",
      location: "Chromepet, Chennai – 600 044, Tamil Nadu",
      scope: "Design, Supply, Installation & Commissioning",
      detail: "Elevated rooftop mounting structure with grid-connected inverter",
    },
    {
      src: "/assets/project-madipakkam.jpeg",
      type: "Residential Rooftop Solar",
      location: "Madipakkam, Chennai – 600 091, Tamil Nadu",
      scope: "Design, Supply, Installation & Commissioning",
      detail: "Elevated rooftop solar structure installed on a residential terrace",
    },
  ],
  solutions: SOLUTIONS,
};

export function buildContent(overrides = {}) {
  const g = (k) => (overrides && overrides[k] != null ? overrides[k] : DEFAULT_CONTENT[k]);
  const site = g("site");
  const images = g("images");
  const SITE = {
    name: "DHIYAA SOLAR ENERGY",
    tagline: site.tagline,
    phone1Display: site.phone1Display,
    phone2Display: site.phone2Display,
    phone1: tel(site.phone1Display),
    phone2: tel(site.phone2Display),
    whatsapp: `https://wa.me/${site.whatsappNumber}?text=${encodeURIComponent(site.whatsappMessage)}`,
    emails: site.emails,
    website: site.website,
    addressLines: site.addressLines,
    gstin: site.gstin,
    udyam: site.udyam,
    mapsEmbed: `https://www.google.com/maps?q=${encodeURIComponent(site.mapsQuery)}&output=embed`,
    mapsLink: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(site.mapsQuery)}`,
  };
  const IMAGES = {
    hero: images.hero,
    pmSurya: images.pmSurya,
    products: images.products,
    solutions: {
      residential: images.residential,
      commercial: images.commercial,
      industrial: images.industrial,
      pump: images.pump,
    },
    projects: g("projects"),
  };
  return {
    SITE,
    IMAGES,
    NAV_LINKS,
    FAQS: g("faqs"),
    PROCESS_STEPS: g("processSteps"),
    BRANDS: g("brands"),
    PRODUCTS: g("products"),
    SOLUTIONS: g("solutions"),
    HOME: g("home"),
    ABOUT: g("about"),
    PM: g("pmSurya"),
  };
}

const ContentCtx = createContext(buildContent({}));

export const ContentProvider = ({ children }) => {
  const [overrides, setOverrides] = useState({});
  useEffect(() => {
    axios
      .get(`${API}/content`)
      .then((r) => setOverrides(r.data || {}))
      .catch(() => {});
  }, []);
  const value = useMemo(() => buildContent(overrides), [overrides]);
  return <ContentCtx.Provider value={value}>{children}</ContentCtx.Provider>;
};

export const useContent = () => useContext(ContentCtx);
